import {
    useState,
    useRef
} from "react";
import {
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type ITextInputRef,
    NCoreUIKitTheme,
    type SheetSide,
    TextInput,
    Button,
    Sheet,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Sheet";

const USAGE_CODE = `const titleRef = useRef<ITextInputRef>(null);

<Sheet
    side="right"
>
    <Sheet.Trigger>
        <Button
            onPress={() => undefined}
            title="Edit task"
        />
    </Sheet.Trigger>
    <Sheet.Content
        initialFocusRef={titleRef}
    >
        <Sheet.Header
            description="MRC-142 · Mobile app"
            title="Task details"
        />
        <Sheet.Body>
            <TextInput
                title="Title"
                ref={titleRef}
            />
        </Sheet.Body>
        <Sheet.Footer>
            <Sheet.Close>
                <Button
                    onPress={() => undefined}
                    title="Cancel"
                    variant="outline"
                />
            </Sheet.Close>
            <Sheet.Close>
                <Button
                    onPress={handleSave}
                    title="Save"
                />
            </Sheet.Close>
        </Sheet.Footer>
    </Sheet.Content>
</Sheet>`;

const CONTROLLED_CODE = `const [isOpen, setIsOpen] = useState(false);

<Sheet
    onOpenChange={setIsOpen}
    isOpen={isOpen}
    side="left"
>
    <Sheet.Content>
        ...
    </Sheet.Content>
</Sheet>`;

const DIRTY_CODE = `<Sheet
    onOpenChange={handleOpenChange}
    isOpen={isOpen}
    isDirty={isDirty}
>
    ...
</Sheet>`;

const SIDES = [
    "right",
    "left",
    "top",
    "bottom"
] as const;

const SIZE_CONFIGS: Array<{
    size?: "medium" | "large";
    widthKey?: "drawerWidth";
    title: string;
}> = [
    {
        title: "size=\"medium\"",
        size: "medium"
    },
    {
        title: "size=\"large\"",
        size: "large"
    },
    {
        title: "width={shapeConfigs.drawerWidth}",
        widthKey: "drawerWidth"
    }
];

type SheetSizeConfig = {
    size?: "medium" | "large";
    width?: number;
};

const SheetSample = () => {
    const {
        t
    } = useDocsI18n();

    return <Sheet.Content>
        <Sheet.Header
            description={t("sample.description")}
            title={t("sample.title")}
        />
        <Sheet.Body>
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {t("sample.item1")}
            </Text>
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {t("sample.item2")}
            </Text>
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {t("sample.item3")}
            </Text>
        </Sheet.Body>
        <Sheet.Footer>
            <Sheet.Close>
                <Button
                    title={t("sample.cancel")}
                    onPress={() => undefined}
                    variant="outline"
                    type="neutral"
                />
            </Sheet.Close>
            <Sheet.Close>
                <Button
                    title={t("sample.action")}
                    onPress={() => undefined}
                />
            </Sheet.Close>
        </Sheet.Footer>
    </Sheet.Content>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("sheetPage.sidesTitle"),
        id: "sides"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("sheetPage.dirtyTitle"),
        id: "dirty"
    },
    {
        title: t("sheetPage.inlineTitle"),
        id: "inline"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SheetPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const taskFieldRef = useRef<ITextInputRef>(null);

    const [
        savedTask,
        setSavedTask
    ] = useState<string | null>(null);

    const [
        draftTask,
        setDraftTask
    ] = useState<string | null>(null);

    const [
        side,
        setSide
    ] = useState<SheetSide>("right");

    const [
        isSideOpen,
        setIsSideOpen
    ] = useState(false);

    const [
        sizeConfig,
        setSizeConfig
    ] = useState<SheetSizeConfig>({});

    const [
        isSizeOpen,
        setIsSizeOpen
    ] = useState(false);

    const [
        savedNote,
        setSavedNote
    ] = useState<string | null>(null);

    const [
        draftNote,
        setDraftNote
    ] = useState<string | null>(null);

    const [
        isNoteOpen,
        setIsNoteOpen
    ] = useState(false);

    const [
        isInlineOpen,
        setIsInlineOpen
    ] = useState(false);

    const taskTitle = savedTask ?? t("sheetPage.taskValue");

    const noteValue = savedNote ?? t("sheetPage.noteValue");

    const isNoteDirty = draftNote !== null && draftNote !== noteValue;

    const handleTaskOpenChange = (isOpen: boolean) => {
        if(isOpen) {
            setDraftTask(null);
        }
    };

    const handleTaskSave = () => {
        if(draftTask !== null) {
            setSavedTask(draftTask);
        }
    };

    const handleSideOpen = (nextSide: SheetSide) => {
        setSide(nextSide);

        setIsSideOpen(true);
    };

    const handleSizeOpen = (nextConfig: SheetSizeConfig) => {
        setSizeConfig(nextConfig);

        setIsSizeOpen(true);
    };

    const handleNoteOpenChange = (isOpen: boolean) => {
        setDraftNote(null);

        setIsNoteOpen(isOpen);
    };

    const handleNoteSave = () => {
        if(draftNote !== null) {
            setSavedNote(draftNote);
        }

        setIsNoteOpen(false);
    };

    return <Article>
        <ComponentHeader
            lead={t("sheetPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("sheetPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("sheetPage.overviewCaption")}
            >
                <Sheet
                    onOpenChange={handleTaskOpenChange}
                    side="right"
                >
                    <Sheet.Trigger>
                        <Button
                            title={t("sheetPage.editTask")}
                            onPress={() => undefined}
                        />
                    </Sheet.Trigger>
                    <Sheet.Content
                        initialFocusRef={taskFieldRef}
                    >
                        <Sheet.Header
                            description={t("sheetPage.taskDescription")}
                            title={t("sheetPage.taskTitle")}
                        />
                        <Sheet.Body>
                            <TextInput
                                title={t("sheetPage.taskField")}
                                onChangeText={setDraftTask}
                                initialValue={taskTitle}
                                ref={taskFieldRef}
                            />
                        </Sheet.Body>
                        <Sheet.Footer>
                            <Sheet.Close>
                                <Button
                                    title={t("sample.cancel")}
                                    onPress={() => undefined}
                                    variant="outline"
                                    type="neutral"
                                />
                            </Sheet.Close>
                            <Sheet.Close>
                                <Button
                                    title={t("sample.action")}
                                    onPress={handleTaskSave}
                                />
                            </Sheet.Close>
                        </Sheet.Footer>
                    </Sheet.Content>
                </Sheet>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("sheetPage.currentTask", [
                        taskTitle
                    ])}
                </Text>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("sheetPage.sidesTitle")}
            id="sides"
        >
            <Paragraph>
                {t("sheetPage.sidesText")}
            </Paragraph>
            <Preview>
                {
                    SIDES.map((sideOption) => <Button
                        onPress={() => handleSideOpen(sideOption)}
                        title={sideOption}
                        variant="outline"
                        key={sideOption}
                        type="neutral"
                    />)
                }
                <Sheet
                    onOpenChange={setIsSideOpen}
                    isOpen={isSideOpen}
                    side={side}
                >
                    <SheetSample/>
                </Sheet>
            </Preview>
            <CodeBlock
                code={CONTROLLED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("sheetPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZE_CONFIGS.map((config) => <Button
                        onPress={() => handleSizeOpen({
                            width: config.widthKey ? docsTokens.shapeConfigs[config.widthKey] : undefined,
                            size: config.size
                        })}
                        title={config.title}
                        key={config.title}
                        variant="outline"
                        type="neutral"
                    />)
                }
                <Sheet
                    onOpenChange={setIsSizeOpen}
                    width={sizeConfig.width}
                    size={sizeConfig.size}
                    isOpen={isSizeOpen}
                >
                    <SheetSample/>
                </Sheet>
            </Preview>
        </Section>
        <Section
            title={t("sheetPage.dirtyTitle")}
            id="dirty"
        >
            <Paragraph>
                {t("sheetPage.dirtyText")}
            </Paragraph>
            <Preview
                caption={t("sheetPage.dirtyCaption")}
            >
                <Sheet
                    onOpenChange={handleNoteOpenChange}
                    isDirty={isNoteDirty}
                    isOpen={isNoteOpen}
                >
                    <Sheet.Trigger>
                        <Button
                            title={t("sheetPage.editNote")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                    </Sheet.Trigger>
                    <Sheet.Content>
                        <Sheet.Header
                            description={t("sheetPage.noteDescription")}
                            title={t("sheetPage.noteTitle")}
                        />
                        <Sheet.Body>
                            <TextInput
                                initialValue={draftNote ?? noteValue}
                                title={t("sheetPage.noteField")}
                                onChangeText={setDraftNote}
                            />
                        </Sheet.Body>
                        <Sheet.Footer>
                            <Sheet.Close>
                                <Button
                                    title={t("sample.cancel")}
                                    onPress={() => undefined}
                                    variant="outline"
                                    type="neutral"
                                />
                            </Sheet.Close>
                            <Button
                                title={t("sample.action")}
                                onPress={handleNoteSave}
                            />
                        </Sheet.Footer>
                    </Sheet.Content>
                </Sheet>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("sheetPage.savedNote", [
                        noteValue
                    ])}
                </Text>
            </Preview>
            <CodeBlock
                code={DIRTY_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("sheetPage.inlineTitle")}
            id="inline"
        >
            <Paragraph>
                {t("sheetPage.inlineText")}
            </Paragraph>
            <Preview>
                <DemoFrame
                    height={docsTokens.shapeConfigs.chartHeight.large}
                >
                    <View
                        style={{
                            padding: docsTokens.spaces.spacingMd,
                            alignItems: "flex-start"
                        }}
                    >
                        <Button
                            onPress={() => setIsInlineOpen(!isInlineOpen)}
                            title={t("sheetPage.toggleInspector")}
                            variant="outline"
                            type="neutral"
                        />
                    </View>
                    <Sheet
                        width={docsTokens.shapeConfigs.drawerWidth}
                        onOpenChange={setIsInlineOpen}
                        isOpen={isInlineOpen}
                        isModal={false}
                    >
                        <SheetSample/>
                    </Sheet>
                </DemoFrame>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("sheetPage.a11yRole"),
                    t("sheetPage.a11yLabel"),
                    t("sheetPage.a11yTrigger"),
                    t("sheetPage.a11yFocus"),
                    t("sheetPage.a11yDismiss"),
                    t("sheetPage.a11yDirty"),
                    t("sheetPage.a11yInline"),
                    t("sheetPage.a11yWarning"),
                    t("sheetPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"right\" | \"left\" | \"start\" | \"end\" | \"top\" | \"bottom\"",
                        description: t("sheetPage.propSide"),
                        defaultValue: "\"right\"",
                        name: "side"
                    },
                    {
                        description: t("sheetPage.propSize"),
                        type: "\"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("sheetPage.propWidth"),
                        name: "width",
                        type: "number"
                    },
                    {
                        description: t("sheetPage.propIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("sheetPage.propIsDefaultOpen"),
                        defaultValue: "false",
                        name: "isDefaultOpen",
                        type: "boolean"
                    },
                    {
                        description: t("sheetPage.propIsModal"),
                        defaultValue: "true",
                        name: "isModal",
                        type: "boolean"
                    },
                    {
                        description: t("sheetPage.propIsDirty"),
                        defaultValue: "false",
                        name: "isDirty",
                        type: "boolean"
                    },
                    {
                        description: t("sheetPage.propIsCloseOnOverlay"),
                        name: "isCloseOnOverlay",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("sheetPage.propCompactBehaviour"),
                        type: "\"bottomSheet\" | \"keepSide\"",
                        name: "compactBehaviour"
                    },
                    {
                        description: t("sheetPage.propConfirmCloseProps"),
                        type: "Partial<IDialogProps>",
                        name: "confirmCloseProps"
                    },
                    {
                        description: t("sheetPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("sheetPage.propOnClosed"),
                        type: "() => void",
                        name: "onClosed"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("sheetPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("sheetPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("sheetPage.partsNote")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("sheetPage.propTriggerChildren"),
                        name: "Trigger.children",
                        type: "ReactElement"
                    },
                    {
                        description: t("sheetPage.propContentInitialFocusRef"),
                        name: "Content.initialFocusRef",
                        type: "RefObject<{ focus }>"
                    },
                    {
                        description: t("sheetPage.propContentAriaLabel"),
                        name: "Content.aria-label",
                        type: "string"
                    },
                    {
                        description: t("sheetPage.propHeaderTitle"),
                        name: "Header.title",
                        type: "string"
                    },
                    {
                        description: t("sheetPage.propHeaderDescription"),
                        name: "Header.description",
                        type: "string"
                    },
                    {
                        description: t("sheetPage.propHeaderIsShowClose"),
                        name: "Header.isShowClose",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("sheetPage.propBodyProps"),
                        type: "ScrollViewProps",
                        name: "Body"
                    },
                    {
                        description: t("sheetPage.propFooterAlign"),
                        type: "\"between\" | \"stretch\" | \"end\"",
                        defaultValue: "\"end\" / \"stretch\"",
                        name: "Footer.align"
                    },
                    {
                        description: t("sheetPage.propCloseChildren"),
                        name: "Close.children",
                        type: "ReactElement"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("sheetPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SheetPage;
